import { useCallback, useMemo } from "react"
import { DESC } from "helpers/sorts"
import ContributorsDataIntegrator from "gatherers/contributors-data-integrator"
import ContributorsTable from "renderers/contributors-datagrid"
import { INSTRUMENTS } from "connected-components/dashboard/helpers"
import { useTemplateGatherers } from "connected-components/dashboard/hooks/use-template-gatherers"
import { useFactorViewDashboardMetrics } from "helpers/metrics/hooks"
import { useAnalytics } from "connected-components/analytics"
import { useFactorsViewData } from "connected-components/factors-view/hooks/use-factors-view-data"
import { useIntradayState } from "connected-components/dashboard/hooks/use-intraday-state"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { useDrilldownOpener } from "connected-components/dashboard/drilldown/use-drilldown-opener"
import { buildDrilldownIntent } from "connected-components/dashboard/drilldown/build-drilldown-intent"
import { resolveIntradayDrilldownFields } from "connected-components/dashboard/drilldown/resolve-intraday-drilldown-fields"
import { useFactorsViewTableHandlers } from "../../hooks/use-factors-view-table-handlers"
import { FACTORS_VIEW_COMMON_PROPS } from "../../prop-types"

export default function FactorsRiskView({ instrumentsWithMetadata, instrumentColumns, driftDates }) {
  const contextData = useFactorsViewData()
  const { startDate, endDate, base: contextBase, instrument, comparisonInstrument, secondInstrument, filter, normalization, sortMetricId, setSortMetricId, sortDirection, setSortDirection, selectedTableLevels, setSelectedTableLevels, isEndVsStartDateComparison, modelId, intradayBatchMetadata, intradayPricingAsOf } = contextData
  const [{ isIntradayMode }] = useIntradayState()

  const analytics = useAnalytics()
  const { handleSortChanged, handleTableNavigationChanged } = useFactorsViewTableHandlers({ track: analytics.track })
  const panelId = usePanelId()
  const openDrilldown = useDrilldownOpener()

  const fromDate = useMemo(() => {
    if (!driftDates) {
      return startDate
    }
    if (startDate < driftDates.startDate) {
      return startDate
    }
    return driftDates.startDate
  }, [startDate, driftDates])

  const handleDrilldownRowClick = useCallback((factorId, clickPosition) => {
    if (!panelId) return
    openDrilldown(buildDrilldownIntent({
      panelId,
      sourceSurface: "factors.risk",
      title: "Asset View: Risk",
      view: "risk",
      context: {
        modelId,
        instrument,
        comparisonInstrument,
        secondInstrument,
        base: contextBase,
        startDate: fromDate,
        endDate,
        isIntradayMode,
        ...resolveIntradayDrilldownFields(isIntradayMode, intradayBatchMetadata, intradayPricingAsOf),
        filter,
        normalization,
        isEndVsStartDateComparison,
      },
      clickPosition,
      seed: { selectedFactor: factorId },
    }))
  }, [panelId, openDrilldown, modelId, instrument, comparisonInstrument, secondInstrument, contextBase, fromDate, endDate, isIntradayMode, intradayBatchMetadata, intradayPricingAsOf, filter, normalization, isEndVsStartDateComparison])

  const gatherers = useTemplateGatherers(contextData, "factorRisk", {
    useCompositionQueries: false,
    from: startDate,
    driftStartDate: fromDate,
  })

  const metrics = useFactorViewDashboardMetrics(
    [{ category: "factorRisk" }],
    { instrumentColumns, isActive: !!contextBase, instrumentsWithMetadata, isEndVsStartDateComparison },
  )

  const defaultSortSuffix = INSTRUMENTS[instrumentColumns[0]].suffix

  return <ContributorsDataIntegrator
    gatherers={gatherers}
    metrics={metrics}
    Renderer={ContributorsTable}
    rendererProps={{
      groupByName: "Category",
      defaultSort: {
        path: `riskDecomposition${defaultSortSuffix}`,
        direction: DESC,
      },
      disableCategories: true,
      classes: "factors-table",
      idLabel: "Factor",
      hidePagination: true,
      includeDrilldownArrows: true,
      downloadKey: "factors.risk",
      downloadName: "Factors - Risk",
      sortMetricId,
      onSortMetricIdChange: setSortMetricId,
      sortDirection,
      onSortDirectionChange: setSortDirection,
      tableNavigationLevels: selectedTableLevels,
      onTableNavigationChange: setSelectedTableLevels,
      onSortChanged: handleSortChanged,
      onTableNavigationChanged: handleTableNavigationChanged,
      onDrilldownRowClick: handleDrilldownRowClick,
      viewLabel: "Risk",
    }}
  />
}

FactorsRiskView.propTypes = FACTORS_VIEW_COMMON_PROPS
